import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import type { OrgRow, OrgStatus } from '@/api/orgTypes';
import type { Page } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/ui/DataTable';

export const ORG_STATUS_TONE: Record<OrgStatus, 'secondary' | 'outline' | 'destructive'> = {
  ACTIVE: 'secondary',
  SUSPENDED: 'outline',
  CLOSED: 'destructive',
};

const columns: ColumnDef<OrgRow, never>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <Link to={`/orgs/${row.original.id}`} className="font-medium text-primary hover:underline">
        {row.original.name}
      </Link>
    ),
  },
  { id: 'partner', header: 'Partner', cell: ({ row }) => row.original.partner.name },
  { id: 'plan', header: 'Plan', cell: ({ row }) => row.original.plan?.name ?? 'No plan' },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={ORG_STATUS_TONE[row.original.status]}>{row.original.status}</Badge>
    ),
  },
  {
    accessorKey: 'connected_accounts',
    header: 'Accounts',
    meta: { numeric: true },
  },
  { accessorKey: 'members', header: 'Members', meta: { numeric: true } },
];

export function OrgTable({
  page,
  isLoading,
  onPage,
  empty,
}: {
  page: Page<OrgRow> | undefined;
  isLoading: boolean;
  onPage: (page: number) => void;
  empty: string;
}) {
  return (
    <>
      <DataTable
        columns={columns}
        data={page?.items ?? []}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        empty={empty}
      />
      {page && page.total_pages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {page.page + 1} of {page.total_pages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page.page === 0}
              onClick={() => onPage(page.page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page.page + 1 >= page.total_pages}
              onClick={() => onPage(page.page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
